// src/pages/ProviderProfile.js
import React, { useEffect, useState, useMemo, Suspense, lazy } from 'react';
import { useMap, MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import L from 'leaflet';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import CustomStarRating from '../components/CustomStarRating';
import VideoPlayer from '../components/VideoPlayer.js';
import './Home.css';
import './ProviderProfile.css';
import 'leaflet/dist/leaflet.css';

// Lazy-load MapContainer to reduce initial bundle size
const LazyMapContainer = lazy(() => import('react-leaflet').then(module => ({ default: module.MapContainer })));

const API_URL = process.env.REACT_APP_API_URL;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const userIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const providerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const fetchProvider = async (providerId) => {
  const response = await axios.get(
    `${API_URL}/api/provider/${providerId}`,
    { withCredentials: true }
  );
  return response.data;
};

const fetchUser = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/profile/`, {
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    return null; // Return null for anonymous users
  }
};

const fetchRatingsSummary = async (providerId) => {
  const response = await axios.get(
    `${API_URL}/api/provider/${providerId}/ratings_summary/`,
    { withCredentials: true }
  );
  return response.data;
};

const fetchCanChat = async (providerId) => {
  const response = await axios.get(
    `${API_URL}/api/can_chat/${providerId}/`,
    { withCredentials: true }
  );
  return response.data;
};

const fetchProviderImages = async (providerId) => {
  const response = await axios.get(`${API_URL}/api/provider/${providerId}/images/`, { withCredentials: true });
  return response.data;
};

const fetchProviderVideos = async (id) => {
  const response = await axios.get(`${API_URL}/api/provider/${id}/videos/`, { withCredentials: true });
  return response.data;
};

// Optimized geocodeLocation with static Nairobi coords for anonymous users
const geocodeLocation = async (location, queryClient) => {
  if (
    !location ||
    typeof location !== 'string' ||
    location.trim() === '' ||
    location === 'Not set'
  ) {
    console.warn('Invalid or empty location:', location);
    return null;
  }

  // Skip geocoding for Nairobi (common for anonymous users)
  if (location.trim().toLowerCase() === 'nairobi') {
    return { lat: -1.2921, lng: 36.8219 };
  }

  const queryKey = ['geocode', location.trim()];
  const cached = queryClient.getQueryData(queryKey);
  if (cached) {
    return cached;
  }

  const query = `${location.trim()}, Kenya`;
  try {
    await new Promise((resolve) => setTimeout(resolve, 100)); // Debounce
    const response = await axios.get(
      `https://nominatim.openstreetmap.org/search`,
      {
        params: {
          q: query,
          format: 'json',
          limit: 1,
        },
        headers: {
          'User-Agent': 'SErraND/1.0 (contact: admin@serrand.org)',
        },
        withCredentials: false,
      }
    );
    const data = response.data[0];
    if (data) {
      const coords = { lat: parseFloat(data.lat), lng: parseFloat(data.lon) };
      queryClient.setQueryData(queryKey, coords, { staleTime: 24 * 60 * 60 * 1000 });
      return coords;
    }
    toast.error(`Could not geocode location: "${location}". Using default (Nairobi).`);
    return null;
  } catch (error) {
    console.error('Geocoding failed:', error.message);
    toast.error(`Geocoding failed for "${location}". Using default (Nairobi).`);
    return null;
  }
};

const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c).toFixed(2);
};

const MapViewController = ({ userCoords, providerCoords }) => {
  const map = useMap();

  useEffect(() => {
    if (userCoords && providerCoords) {
      const bounds = L.latLngBounds(
        [userCoords.lat, userCoords.lng],
        [providerCoords.lat, providerCoords.lng]
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } else if (providerCoords) {
      map.setView([providerCoords.lat, providerCoords.lng], 13);
    }
  }, [map, userCoords, providerCoords]);

  return null;
};

function ProviderProfile() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [chatInitiated, setChatInitiated] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [userCoords, setUserCoords] = useState(null);
  const [providerCoords, setProviderCoords] = useState(null);
  const [distance, setDistance] = useState(null);
  const [geocodeFailed, setGeocodeFailed] = useState(false);
  const [showScoringInfo, setShowScoringInfo] = useState(false);
  const [showReviewsPanel, setShowReviewsPanel] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const {
    data: provider,
    isLoading: providerLoading,
    error: providerError,
  } = useQuery({
    queryKey: ['provider', providerId],
    queryFn: () => fetchProvider(providerId),
    onError: (err) => {
      console.error('Provider fetch error:', err);
      toast.error(
        err.response?.data?.error || 'Failed to load provider profile.'
      );
    },
  });

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user'],
    queryFn: fetchUser,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: ratingsSummary, isLoading: ratingsLoading } = useQuery({
    queryKey: ['ratingsSummary', providerId],
    queryFn: () => fetchRatingsSummary(providerId),
    enabled: !!provider,
    staleTime: 10 * 60 * 1000,
  });

  const { data: canChatData, isLoading: canChatLoading } = useQuery({
    queryKey: ['canChat', providerId],
    queryFn: () => fetchCanChat(providerId),
    enabled: !!provider && !!user,
    onError: (err) => {
      console.error('CanChat fetch error:', err);
      toast.error(
        err.response?.data?.error || 'Failed to check chat eligibility.'
      );
    },
  });

  const { data: providerImages, isLoading: imagesLoading } = useQuery({
    queryKey: ['providerImages', providerId],
    queryFn: () => fetchProviderImages(providerId),
    enabled: !!provider,
    staleTime: 10 * 60 * 1000,
  });

  const { data: providerVideos = [], isLoading: videosLoading } = useQuery({
    queryKey: ['providerVideos', providerId],
    queryFn: () => fetchProviderVideos(providerId),
    enabled: !!provider,
    staleTime: 10 * 60 * 1000,
  });

  const fetchCoordinates = async () => {
    const defaultCoords = { lat: -1.2921, lng: 36.8219 }; // Nairobi

    if (user?.latitude && user?.longitude) {
      setUserCoords({ lat: user.latitude, lng: user.longitude });
    } else {
      const userLocation = user?.location || 'Nairobi';
      if (userLocation.toLowerCase() === 'nairobi') {
        setUserCoords(defaultCoords);
      } else {
        const coords = await geocodeLocation(userLocation, queryClient);
        setUserCoords(coords || defaultCoords);
      }
    }

    if (provider?.latitude && provider?.longitude) {
      setProviderCoords({ lat: provider.latitude, lng: provider.longitude });
      setGeocodeFailed(false);
    } else {
      const providerLocation = provider?.location || 'Nairobi';
      if (providerLocation === 'Not set' || providerLocation.toLowerCase() === 'nairobi') {
        setProviderCoords(defaultCoords);
        setGeocodeFailed(providerLocation !== 'Not set');
      } else {
        const coords = await geocodeLocation(providerLocation, queryClient);
        setProviderCoords(coords || defaultCoords);
        setGeocodeFailed(!coords);
      }
    }
  };

  useEffect(() => {
    if (provider) {
      fetchCoordinates();
    }
  }, [provider, user]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('order') === 'true') {
      setShowOrderForm(true);
      // Clear query param to prevent re-triggering on refresh
      navigate(`/provider/${providerId}`, { replace: true });
    }
  }, [location, navigate, providerId]);

  const calculatedDistance = useMemo(() => {
    if (userCoords && providerCoords) {
      return calculateDistance(
        userCoords.lat,
        userCoords.lng,
        providerCoords.lat,
        providerCoords.lng
      );
    }
    return null;
  }, [userCoords, providerCoords]);

  useEffect(() => {
    setDistance(calculatedDistance);
  }, [calculatedDistance]);

  const handleChatClick = () => {
    if (!user) {
      toast.error(
        'Please log in to start a chat. Open the hamburger menu (on mobile) or use the navbar (on desktop) to log in or register.'
      );
      navigate('/', { state: { showLogin: true } });
      return;
    }
    if (!canChatData?.can_chat) {
      toast.error(
        canChatData?.error || 'Chat is only available during an active order.'
      );
      return;
    }
    setChatInitiated(true);
    navigate(`/profile?chat_with=${provider.user.id}`);
    toast.success('Opening chat.');
  };

  const handleOrderService = async () => {
    if (!user) {
      toast.error(
        'It seems like you\'re trying to order a service without being logged in. Please open the hamburger menu (on mobile) or use the navbar (on desktop) to log in or register.'
      );
      navigate('/', { state: { showLogin: true } });
      return;
    }
    if (!description || !scheduledAt) {
      toast.error('Please fill in all fields.');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/order_service/${providerId}/`,
        { description, scheduled_at: scheduledAt },
        { withCredentials: true }
      );
      toast.success('Service request submitted!');
      setShowOrderForm(false);
      setDescription('');
      setScheduledAt('');
      navigate('/manage-orders');
    } catch (err) {
      console.error('Order failed:', err.response?.data || err.message);
      toast.error(err.response?.data?.error || 'Failed to submit order.');
    }
  };

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.removeQueries(['user']);
      queryClient.setQueryData(['user'], null);
      navigate('/');
      toast.success('Logged out successfully.');
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Image navigation handlers
  const handleNextImage = () => {
    if (providerImages && selectedImageIndex !== null) {
      setSelectedImageIndex((prev) => (prev + 1) % providerImages.length);
    }
  };

  const handlePrevImage = () => {
    if (providerImages && selectedImageIndex !== null) {
      setSelectedImageIndex((prev) => (prev - 1 + providerImages.length) % providerImages.length);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (selectedImageIndex !== null) {
        if (e.key === 'ArrowRight') handleNextImage();
        if (e.key === 'ArrowLeft') handlePrevImage();
        if (e.key === 'Escape') setSelectedImageIndex(null);
      }
      if (selectedVideo !== null && e.key === 'Escape') {
        setSelectedVideo(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedImageIndex, selectedVideo, providerImages]);

  const trustColor = useMemo(
    () =>
      ['A', 'B'].includes(provider?.trust_tier)
        ? 'text-green-400'
        : provider?.trust_tier === 'E'
        ? 'text-red-400'
        : 'text-yellow-400',
    [provider]
  );
  const isTopTier = useMemo(
    () => ['A', 'B'].includes(provider?.trust_tier),
    [provider]
  );
  const recentReview = provider?.recent_review;
  const allReviews = provider?.all_reviews || [];

  const pageTitle = provider
    ? `${provider.business_name} - Service Provider Profile | SErraND`
    : 'Provider Profile | SErraND';
  const pageDescription = provider
    ? `${
        provider.description?.slice(0, 160) ||
        `Hire ${provider.business_name} for ${
          provider.service_provided || 'reliable services'
        } in ${provider.location || 'Kenya'}.`
      }...`
    : 'Connect with trusted service providers in Kenya on SErraND. Find reliable professionals for all your needs.';
  const pageKeywords = provider
    ? `${
        provider.service_provided || 'service provider'
      }, ${provider.business_name}, ${provider.location || 'Kenya'}, SErraND, local services, hire professional`
    : 'SErraND, service providers, Kenya, local services, hire professionals, trusted gurus';
  const canonicalUrl = `https://www.serrand.org/provider/${providerId}`;
  const ogImage =
    provider?.user?.profile_picture ||
    'https://www.serrand.org/static/images/og-image.jpg';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': provider?.is_individual ? 'Person' : 'Organization',
      name: provider?.business_name || 'Service Provider',
      description:
        provider?.description ||
        `Provider of ${provider?.service_provided || 'services'} in ${
          provider?.location || 'Kenya'
        }.`,
      url: canonicalUrl,
      address: {
        '@type': 'PostalAddress',
        addressLocality: provider?.location || 'Nairobi',
        addressCountry: 'KE',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        email: provider?.email || 'support@serrand.org',
      },
      image: ogImage,
    },
    publisher: {
      '@type': 'Organization',
      name: 'SErraND',
      url: 'https://www.serrand.org',
      logo: 'https://www.serrand.org/images/logo.png',
    },
  };

  if (providerLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white text-xl">Loading provider details...</p>
        </div>
      </div>
    );
  }
  if (!provider || providerError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-xl p-8 max-w-md mx-auto">
            <h3 className="text-xl font-bold text-red-400 mb-2">Provider Not Found</h3>
            <p className="text-red-300 mb-4">The requested provider profile could not be loaded.</p>
            <Link 
              to="/" 
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all inline-block"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Marvin Useki" />
        <meta name="viewport" content="width=device-width, initial-scale=0.9, maximum-scale=1.0, user-scalable=yes" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="SErraND" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:site" content="@SErraND" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Enhanced Navigation */}
      <nav className="relative z-40 bg-gray-900 bg-opacity-80 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                <span className="text-2xl font-bold">SE</span>
                <span className="text-2xl font-bold">RRA</span>
                <span className="text-2xl font-bold">ND</span>
              </div>
            </Link>

            {user?.username && (
              <span className="md:hidden text-gray-300 text-sm">
                Jambo, <span className="font-semibold text-white">{user.username}</span>! 👋
              </span>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden relative w-8 h-8 flex flex-col justify-center items-center"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-0.5' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </button>

            {/* Desktop navigation */}
            <div className="hidden md:flex md:items-center md:space-x-6">
              <Link 
                to="/" 
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Home
              </Link>
              <Link 
                to="/profile" 
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Profile
              </Link>
              <Link 
                to="/contact" 
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Contact
              </Link>
              
              {userLoading || isAuthLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-400 text-sm">Loading...</span>
                </div>
              ) : user?.username ? (
                <div className="flex items-center space-x-4">
                  <span className="text-gray-300 text-sm">
                    Jambo, <span className="font-semibold text-white">{user.username}</span>! 👋
                  </span>
                  <button
                    onClick={handleLogout}
                    className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => navigate('/', { state: { showLogin: true } })}
                    className="text-gray-300 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-800"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/', { state: { showRegister: true } })}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all"
                  >
                    Register
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40" 
              onClick={closeMenu}
              onTouchEnd={closeMenu}
            ></div>
            <div className="absolute top-full left-0 right-0 bg-gray-900 bg-opacity-95 backdrop-blur-lg border-t border-gray-700 shadow-2xl z-50">
              <div className="px-4 py-6 space-y-4">
                <Link 
                  to="/" 
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800" 
                  onClick={closeMenu}
                >
                  Home
                </Link>
                <Link 
                  to="/profile" 
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800" 
                  onClick={closeMenu}
                >
                  Profile
                </Link>
                <Link 
                  to="/contact" 
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800" 
                  onClick={closeMenu}
                >
                  Contact
                </Link>
                
                {userLoading || isAuthLoading ? (
                  <div className="flex items-center justify-center space-x-2 py-4">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-400">Loading...</span>
                  </div>
                ) : user?.username ? (
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMenu();
                    }}
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3 rounded-lg font-medium hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
                  >
                    Logout
                  </button>
                ) : (
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        navigate('/', { state: { showLogin: true } });
                        closeMenu();
                      }}
                      className="w-full text-gray-300 hover:text-white px-4 py-3 rounded-lg font-medium transition-colors hover:bg-gray-800 border border-gray-600"
                    >
                      Login
                    </button>
                    <button
                      onClick={() => {
                        navigate('/', { state: { showRegister: true } });
                        closeMenu();
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-lg font-medium hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                    >
                      Register
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative py-16 px-4 text-center">
        <div className="relative z-10">
          <h1 className="text-5xl md:text-7xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
              {provider.business_name}
            </span>
            {isTopTier && (
              <span className="block text-2xl md:text-3xl text-yellow-400 mt-2">
                ⭐ Top-Rated Guru
              </span>
            )}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-2">
            {provider.service_provided || 'Professional Service Provider'}
          </p>
          <p className="text-lg text-gray-400 mb-8">
            📍 {provider.location || 'Kenya'} • Trust Score: <span className={trustColor}>{provider.trust_score?.toFixed(1) || '0.0'}</span>
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Provider Details Card */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-8 shadow-2xl backdrop-blur-lg">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl opacity-0 hover:opacity-5 transition-opacity"></div>
                <div className="relative z-10">
                  <div className="flex flex-col md:flex-row md:items-start gap-6 mb-8">
                    <div className="flex-1">
                      <h2 className="text-3xl font-bold text-white mb-4">{provider.business_name}</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
                        <div>
                          <p className="mb-2"><strong className="text-blue-400">Username:</strong> {provider.user.username}</p>
                          <p className="mb-2"><strong className="text-blue-400">Service:</strong> {provider.service_provided || 'Not set'}</p>
                          <p className="mb-2"><strong className="text-blue-400">Location:</strong> {provider.location || 'Not set'}</p>
                        </div>
                        <div>
                          <p className="mb-2"><strong className="text-blue-400">Email:</strong> {provider.email || 'Not provided'}</p>
                          <p className="mb-2"><strong className="text-blue-400">Hours:</strong> {provider.business_hours || 'Not set'}</p>
                          <p className="mb-2">
                            <strong className="text-blue-400">Trust Score:</strong>{' '}
                            <span className={trustColor}>
                              {provider.trust_score?.toFixed(1) || '0.0'} (Tier {provider.trust_tier || 'E'})
                            </span>
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-6 flex items-center gap-4">
                        <div className="flex items-center">
                          <CustomStarRating
                            count={5}
                            value={provider.average_rating || 0}
                            onChange={() => {}}
                            size={20}
                          />
                          <span className="ml-2 text-gray-300">
                            {provider.average_rating?.toFixed(1) || '0.0'}/5 ({provider.rating_count} reviews)
                          </span>
                        </div>
                        {distance && (
                          <span className="text-purple-400 bg-purple-400 bg-opacity-20 px-3 py-1 rounded-full text-sm">
                            📍 {distance} km away
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {provider.description && (
                    <div className="mb-8 p-6 bg-gray-800 bg-opacity-50 rounded-xl border border-gray-600">
                      <h3 className="text-xl font-semibold text-white mb-3">About This Service</h3>
                      <p className="text-gray-300 leading-relaxed">
                        {provider.description}
                      </p>
                    </div>
                  )}

                  {/* Recent Review */}
                  {recentReview ? (
                    <div className="mb-8 p-6 bg-gradient-to-br from-purple-800 to-purple-900 bg-opacity-30 rounded-xl border border-purple-500 border-opacity-30">
                      <h3 className="text-xl font-semibold text-white mb-4">Most Recent Review ⭐</h3>
                      <div className="flex items-center mb-3">
                        <CustomStarRating
                          count={5}
                          value={recentReview.rating}
                          onChange={() => {}}
                          size={18}
                        />
                        <span className="ml-2 text-purple-300 font-medium">
                          by {recentReview.display_name}
                        </span>
                      </div>
                      <p className="text-gray-200 italic">
                        "{recentReview.review || 'Great service!'}"
                      </p>
                    </div>
                  ) : (
                    <div className="mb-8 p-6 bg-gray-800 bg-opacity-30 rounded-xl border border-gray-600 text-center">
                      <p className="text-gray-400 text-lg">No reviews yet - be the first to hire this guru! 🌟</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <button
                      onClick={handleChatClick}
                      className={`bg-gradient-to-r from-purple-500 to-purple-600 text-white py-4 px-6 rounded-xl font-bold hover:from-purple-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all shadow-lg ${
                        !user || !canChatData?.can_chat || canChatLoading
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      disabled={!user || !canChatData?.can_chat || canChatLoading}
                      title={!user ? 'Log in to start a chat' : ''}
                    >
                      💬 {chatInitiated ? 'Continue Chat' : 'Start Chat'}
                    </button>
                    <button
                      onClick={() => setShowOrderForm(true)}
                      className="bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-xl font-bold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all shadow-lg"
                    >
                      🛒 Order Service
                    </button>
                    <button
                      onClick={() => setShowScoringInfo(true)}
                      className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 px-6 rounded-xl font-bold hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all shadow-lg"
                    >
                      ℹ️ How Scoring Works
                    </button>
                  </div>

                  {provider.rating_count > 0 && (
                    <div className="text-center">
                      <button
                        onClick={() => setShowReviewsPanel(true)}
                        className="text-purple-400 hover:text-purple-300 transition-colors font-medium text-lg hover:underline"
                      >
                        View All Reviews ({provider.rating_count}) →
                      </button>
                    </div>
                  )}

                  {/* MEDIA SECTION: VIDEOS + IMAGES */}
                  <div className="mt-12 space-y-12">
                    {/* VIDEOS */}
                    {videosLoading ? (
                      <div className="text-center py-8 text-gray-400">Loading videos...</div>
                    ) : Array.isArray(providerVideos) && providerVideos.length > 0 ? (
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                          Service Videos
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {providerVideos.map((video) => (
                            <div
                              key={video.id}
                              className="relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer group bg-black"
                              onClick={() => setSelectedVideo(video)}
                            >
                              <VideoPlayer url={video.video} className="w-full h-64 object-cover rounded-2xl" />
                              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <div className="bg-white text-black rounded-full w-16 h-16 flex items-center justify-center shadow-2xl">
                                  <span className="text-3xl ml-1">Play</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {/* IMAGES GALLERY */}
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-6">
                        Gallery{' '}
                        {providerImages && providerImages.length > 0 && (
                          <span className="text-gray-400 text-lg">({providerImages.length})</span>
                        )}
                      </h3>

                      {imagesLoading ? (
                        <div className="text-center py-8 text-gray-400">Loading images...</div>
                      ) : Array.isArray(providerImages) && providerImages.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                          {providerImages.map((img, index) => (
                            <div
                              key={img.id}
                              onClick={() => setSelectedImageIndex(index)}
                              className="relative overflow-hidden rounded-xl shadow-lg cursor-pointer group aspect-square"
                            >
                              <img
                                src={img.image}
                                alt={`${provider.business_name} gallery ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                                <span className="text-white text-5xl opacity-0 group-hover:opacity-100 transition-opacity">Zoom</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 bg-gray-800 bg-opacity-40 rounded-xl border border-gray-700">
                          <p className="text-gray-400 text-lg">No photos uploaded yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Map Card */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-6 shadow-2xl backdrop-blur-lg sticky top-4">
              <h3 className="text-2xl font-bold text-white mb-4">📍 Location</h3>
              {userCoords && providerCoords ? (
                <Suspense fallback={
                  <div className="h-80 bg-gray-700 rounded-xl flex items-center justify-center">
                    <div className="text-white text-center">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                      Loading map...
                    </div>
                  </div>
                }>
                  <div className="h-80 rounded-xl overflow-hidden border border-gray-600">
                    <LazyMapContainer
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom={false}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      />
                      <MapViewController
                        userCoords={userCoords}
                        providerCoords={providerCoords}
                      />
                      <Marker position={[userCoords.lat, userCoords.lng]} icon={userIcon}>
                        <Popup>
                          <div className="text-center p-2">
                            <strong>Your Location</strong>
                            <br />
                            {user?.location || 'Default: Nairobi'}
                          </div>
                        </Popup>
                      </Marker>
                      <Marker
                        position={[providerCoords.lat, providerCoords.lng]}
                        icon={providerIcon}
                      >
                        <Popup>
                          <div className="text-center p-2">
                            <strong>{provider.business_name}</strong>
                            <br />
                            {provider.location || 'Default: Nairobi'}
                          </div>
                        </Popup>
                      </Marker>
                    </LazyMapContainer>
                  </div>
                  {distance && (
                    <div className="mt-4 p-3 bg-gradient-to-r from-blue-500 to-purple-600 bg-opacity-20 rounded-lg border border-blue-400 border-opacity-30">
                      <p className="text-white text-center">
                        <strong>Distance:</strong> {distance} km away
                      </p>
                    </div>
                  )}
                  {geocodeFailed && provider.location !== 'Not set' && (
                    <div className="mt-4 p-3 bg-yellow-500 bg-opacity-20 rounded-lg border border-yellow-400 border-opacity-30">
                      <p className="text-yellow-300 text-sm text-center">
                        ⚠️ Couldn't find "{provider.location}" - showing Nairobi instead
                      </p>
                    </div>
                  )}
                </Suspense>
              ) : (
                <div className="h-80 bg-gray-700 rounded-xl flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    Loading location data...
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Order Form Modal */}
      {showOrderForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700 backdrop-blur-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-3xl font-bold text-white bg-gradient-to-r from-green-400 to-blue-600 bg-clip-text text-transparent">
                  Order Service 🛒
                </h3>
                <button
                  onClick={() => {
                    setShowOrderForm(false);
                    setDescription('');
                    setScheduledAt('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Service Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what you need..."
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all h-24 resize-none"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Schedule Date and Time
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleOrderService}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-bold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                  >
                    Submit Order
                  </button>
                  <button
                    onClick={() => {
                      setShowOrderForm(false);
                      setDescription('');
                      setScheduledAt('');
                    }}
                    className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scoring Info Modal */}
      {showScoringInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700 backdrop-blur-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-white bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                  How Our Scoring Works ⭐
                </h2>
                <button
                  onClick={() => setShowScoringInfo(false)}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6 text-gray-300">
                <div className="p-4 bg-gradient-to-r from-green-500 to-green-600 bg-opacity-20 rounded-lg border border-green-400 border-opacity-30">
                  <h3 className="text-lg font-semibold text-green-400 mb-2">Quality Ratings</h3>
                  <p>After completing an order, clients rate providers (1-5 stars) with optional reviews. These ratings directly impact the provider's trust score and help other users make informed decisions.</p>
                </div>
                
                <div className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 bg-opacity-20 rounded-lg border border-blue-400 border-opacity-30">
                  <h3 className="text-lg font-semibold text-blue-400 mb-2">Provider Trust Score (0-100)</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li><strong>Quality Ratings (40%):</strong> Average of all client ratings</li>
                    <li><strong>Transaction Honesty (20%):</strong> Lower cash payment usage increases score</li>
                    <li><strong>Service Smoothness (20%):</strong> Based on acceptance, completion, and on-time rates</li>
                    <li><strong>M-Pesa Preference (20%):</strong> Using M-Pesa payment boosts the score</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-gradient-to-r from-purple-500 to-purple-600 bg-opacity-20 rounded-lg border border-purple-400 border-opacity-30">
                  <h3 className="text-lg font-semibold text-purple-400 mb-2">Trust Tiers</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><strong className="text-red-400">E (≤30):</strong> New/Low</div>
                    <div><strong className="text-orange-400">D (≤45):</strong> Below Average</div>
                    <div><strong className="text-yellow-400">C (≤60):</strong> Average</div>
                    <div><strong className="text-green-400">B (≤80):</strong> Good</div>
                    <div><strong className="text-emerald-400">A (&gt;80):</strong> Excellent</div>
                  </div>
                  <p className="mt-2 text-sm">Higher tiers improve visibility in search results.</p>
                </div>
                
                <div className="p-4 bg-gradient-to-r from-yellow-500 to-yellow-600 bg-opacity-20 rounded-lg border border-yellow-400 border-opacity-30">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-2">User Reputation Score (0-100)</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Base score: 50 points</li>
                    <li><strong>Completions:</strong> +30% per completed order</li>
                    <li><strong>Cancellations/Rejections:</strong> -20% per cancellation</li>
                  </ul>
                  <p className="mt-2">Higher scores show you're a reliable client and may get priority service.</p>
                </div>
              </div>
              
              <button
                onClick={() => setShowScoringInfo(false)}
                className="w-full mt-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-bold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-all"
              >
                Got It! 👍
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Panel Modal */}
      {showReviewsPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700 backdrop-blur-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  All Reviews for <span className="bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">{provider.business_name}</span>
                </h2>
                <button
                  onClick={() => setShowReviewsPanel(false)}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
              
              {allReviews.length > 0 ? (
                <div className="space-y-4">
                  {allReviews.map((review) => (
                    <div key={review.id} className="p-4 bg-gradient-to-r from-gray-700 to-gray-800 rounded-xl border border-gray-600">
                      <div className="flex items-center mb-2">
                        <CustomStarRating
                          count={5}
                          value={review.rating}
                          onChange={() => {}}
                          size={16}
                        />
                        <span className="ml-2 text-purple-400 font-medium">
                          by {review.display_name}
                        </span>
                      </div>
                      <p className="text-gray-200 mb-2">
                        {review.review || 'No review text provided.'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-lg">No reviews available yet.</p>
                </div>
              )}
              
              <button
                onClick={() => setShowReviewsPanel(false)}
                className="w-full mt-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white py-3 px-6 rounded-lg font-bold hover:from-purple-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-all"
              >
                Close Reviews
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {selectedImageIndex !== null && Array.isArray(providerImages) && providerImages[selectedImageIndex] && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setSelectedImageIndex(null)}
          role="dialog"
          aria-label="Image gallery modal"
        >
          <div
            className="relative max-w-4xl w-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 text-white text-3xl bg-gray-800 bg-opacity-80 rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-700 transition backdrop-blur-lg z-10"
              onClick={() => setSelectedImageIndex(null)}
              aria-label="Close image gallery"
            >
              ×
            </button>
            {/* Navigation buttons */}
            {Array.isArray(providerImages) && providerImages.length > 1 && (
              <>
                <button
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white text-2xl bg-gray-800 bg-opacity-80 rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-700 transition backdrop-blur-lg z-10"
                  onClick={handlePrevImage}
                  aria-label="Previous image"
                >
                  ←
                </button>
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white text-2xl bg-gray-800 bg-opacity-80 rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-700 transition backdrop-blur-lg z-10"
                  onClick={handleNextImage}
                  aria-label="Next image"
                >
                  →
                </button>
              </>
            )}
            
            <div className="relative">
              <img
                src={providerImages[selectedImageIndex].image}
                alt={`${provider.business_name} Service ${selectedImageIndex + 1}`}
                className="w-full h-auto max-h-[80vh] object-contain rounded-xl shadow-2xl"
              />
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 bg-opacity-80 text-white px-4 py-2 rounded-full backdrop-blur-lg">
                {selectedImageIndex + 1} of {providerImages.length}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIDEO FULLSCREEN MODAL */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute top-4 right-4 text-white text-5xl bg-black bg-opacity-70 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-90 transition z-10"
            >
              ×
            </button>
            <VideoPlayer url={selectedVideo.video} className="w-full rounded-2xl shadow-2xl" />
          </div>
        </div>
      )}


      {/* Enhanced Footer */}
      <footer className="bg-gray-900 bg-opacity-90 backdrop-blur-lg border-t border-gray-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4 group">
                <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                  <span className="text-3xl font-bold">SE</span>
                  <span className="text-3xl font-bold">RRA</span>
                  <span className="text-3xl font-bold">ND</span>
                </div>
              </Link>
              <p className="text-gray-300 text-lg mb-4 leading-relaxed">
                Your trusted platform for connecting with local service providers across Kenya. 
                From quick errands to major projects, we've got you covered! 🇰🇪
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/profile" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Profile
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal & Support */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Legal & Support</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    About Us
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-700 pt-8 mt-8 text-center">
            <p className="text-gray-400">
              © 2025 SErraND. All rights reserved. Made with ❤️ in Kenya 🇰🇪
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Connecting communities, one errand at a time.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ProviderProfile;
